import RestaurantCard from "@/components/RestaurantCard";
import { useCallback, useEffect, useRef, useState, type FC } from "react";
import restaurantListMock from "@/mocks/restaurant-list";
import { cn, formatPrice } from "@/lib/utils";
import useDetectSizeDevice from "@/hooks/useDetectSizeDevice";
import Text from "@/components/Text";
import { StoreIcon } from "lucide-react";
import { type MapPin, type Restaurant } from "@/API";
import SkeletonRestaurantListInExplorer from "@/components/skeletons/SkeletonRestaurantListInExplorer";
import type { ViewMode } from "../..";
import { Skeleton } from "@/components/ui/skeleton";
import { useVirtualizer } from "@tanstack/react-virtual";
import { generatePath, useNavigate } from "react-router";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import useIsAuthWrapper from "@/hooks/useIsAuthWrapper";
import AddRestaurantToFavorite from "@/components/dialogs/AddRestaurantToFavorite";
import { userFavoriteListState } from "@/store/userFavoriteListState";
import DrawerAddRestaurantToFavorite from "@/components/drawers/DrawerAddRestaurantToFavorite";
import { useTranslation } from "react-i18next";
import type { DrawerState } from "../DrawerRestaurantsList";

type Props = {
  isLoading: boolean;
  restaurants?: Restaurant[] | null;
  viewMode: ViewMode;
  totalRestaurant: number;
  isFetching: boolean;
  fetchRestaurant: (append?: boolean) => void;
  restaurantsError?: Error | null;
  drawerState?: DrawerState;
  pins: MapPin[] | null;
};

const getHeightMap = (drawerState: DrawerState) => {
  if (drawerState === "open") return "calc(100dvh - 280px)";
  if (drawerState === "half-open") return "calc(100dvh / 2 - 140px)";
  return 0;
};

const getHeightList = (drawerState: DrawerState) => {
  if (drawerState === "open") return "calc(100dvh - 235px)";
  if (drawerState === "half-open") return "calc(100dvh / 2 - 95px)";
  return 0;
};

const RestaurantList: FC<Props> = ({
  restaurants,
  isLoading,
  viewMode,
  totalRestaurant,
  isFetching,
  fetchRestaurant,
  restaurantsError,
  drawerState,
  pins,
}) => {
  const { sm, xxl } = useDetectSizeDevice();
  const [addFavoriteThisReastaurantId, setAddFavoriteThisReastaurantId] =
    useState<string | null>(null);
  const navigate = useNavigate();
  const isAuthWrapperRoute = useIsAuthWrapper();
  const { data: favoriteListRestaurants } = userFavoriteListState();
  const { t } = useTranslation();

  const favoriteRestaurantsId = favoriteListRestaurants
    ? Object.values(favoriteListRestaurants)
        .map((favoriteRestaurant) => favoriteRestaurant.restaurants)
        .flat()
        .map((restaurant) => restaurant.id)
    : [];

  const parentRef = useRef<HTMLDivElement>(null);

  const moduloStep = xxl ? 3 : sm ? 2 : 1;
  const restaurantsGrid: Restaurant[][] = [];

  if (Array.isArray(restaurants)) {
    restaurants.forEach((restaurant, index) => {
      if (index % moduloStep === 0) {
        restaurantsGrid.push([restaurant]);
      } else {
        restaurantsGrid[restaurantsGrid.length - 1].push(restaurant);
      }
    });
  }

  const rowVirtualizer = useVirtualizer({
    count: restaurantsGrid.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => {
      if (parentRef.current?.clientWidth) {
        return moduloStep === 3
          ? 250
          : moduloStep === 2
            ? parentRef.current?.clientWidth / 1.88
            : 340;
      }
      return moduloStep === 3 ? 250 : moduloStep === 2 ? 220 : 340;
    },
    measureElement:
      typeof window !== "undefined" &&
      navigator.userAgent.indexOf("Firefox") === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
  });

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement && Array.isArray(restaurants)) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        if (
          scrollHeight - scrollTop - clientHeight < 50 &&
          !isFetching &&
          restaurants.length < totalRestaurant
        ) {
          fetchRestaurant(true);
        }
      }
    },
    [fetchRestaurant, isFetching, restaurants, totalRestaurant],
  );

  useEffect(() => {
    fetchMoreOnBottomReached(parentRef.current);
  }, [fetchMoreOnBottomReached]);

  if (!restaurants && restaurantsError && !isAuthWrapperRoute) {
    return (
      <div
        style={{ marginTop: 20 }}
        className={cn(
          viewMode === "map" ? "w-[0px]" : "w-[100%] sm:w-[50%]",
          viewMode === "map" ? "p-[0px]" : "p-[16px] sm:px-[24px] sm:py-[0px]",
        )}
      >
        <Alert variant="destructive">
          <AlertTitle>{t("error_loading_restaurants")}</AlertTitle>
          <AlertDescription>
            {`${t("error")}: ${restaurantsError.message}`}
          </AlertDescription>
          <AlertDescription>
            {t("please_try_again_or_change_filters")}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const numberDeliverooRestaurant =
    pins?.filter((pin) => pin.platform === "DELIVEROO")?.length ?? 0;
  const numberUbereatsRestaurant =
    pins?.filter((pin) => pin.platform === "UBEREATS")?.length ?? 0;

  return (
    <>
      {sm ? (
        <AddRestaurantToFavorite
          open={!!addFavoriteThisReastaurantId}
          onClose={() => setAddFavoriteThisReastaurantId(null)}
          restaurantsId={
            addFavoriteThisReastaurantId ? [addFavoriteThisReastaurantId] : []
          }
        />
      ) : (
        <DrawerAddRestaurantToFavorite
          open={!!addFavoriteThisReastaurantId}
          onClose={() => setAddFavoriteThisReastaurantId(null)}
          restaurantsId={
            addFavoriteThisReastaurantId ? [addFavoriteThisReastaurantId] : []
          }
        />
      )}

      {isLoading ? (
        <Skeleton className="w-[100%] h-[100px] rounded-[12px] mb-[24px] sm:hidden" />
      ) : (
        <div className="sm:hidden w-[100%] flex justify-center items-center mb-[16px]">
          <div className="flex items-center gap-[4px]">
            <div className="flex bg-[#FCFCFD] p-[8px] rounded-[8px] items-center gap-[4px]">
              <StoreIcon height={16} width={16} color="#758195" />
              <Text fontWeight={600} fontSize={12}>
                {Array.isArray(restaurants)
                  ? formatPrice(totalRestaurant.toString())
                  : restaurantListMock.length}
              </Text>
            </div>
            <div className="flex bg-[#FCFCFD] p-[8px] rounded-[8px] items-center gap-[4px]">
              <img
                height={16}
                width={16}
                className="rounded-[4px]"
                src="/partner/ubereats.png"
              />
              <Text fontWeight={600} fontSize={12}>
                {`${
                  numberUbereatsRestaurant === 0
                    ? 0
                    : (
                        (numberUbereatsRestaurant /
                          (numberUbereatsRestaurant +
                            numberDeliverooRestaurant)) *
                        100
                      ).toFixed(0)
                } %`}
              </Text>
            </div>
            <div className="flex bg-[#FCFCFD] p-[8px] rounded-[8px] items-center gap-[4px]">
              <img
                height={16}
                width={16}
                className="rounded-[4px]"
                src="/partner/deliveroo.png"
              />
              <Text fontWeight={600} fontSize={12}>
                {`${
                  numberDeliverooRestaurant === 0
                    ? 0
                    : (
                        (numberDeliverooRestaurant /
                          (numberUbereatsRestaurant +
                            numberDeliverooRestaurant)) *
                        100
                      ).toFixed(0)
                } %`}
              </Text>
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          height: sm
            ? "calc(100vh - var(--height-header) - 60px)"
            : getHeightList(drawerState ?? "half-open"),
          gap: viewMode === "map" ? "0px" : "16px",
          width: sm ? (viewMode === "map" ? "0px" : "50%") : "100%",
          transition: "width 1s",
        }}
        className={cn(
          sm ? "bg-[#F7F8F9]" : "bg-[white]",
          viewMode === "map" ? "w-[0px]" : "w-[100%] sm:w-[50%]",
          viewMode === "map" ? "p-[0px]" : "px-[16px] sm:px-[24px] sm:py-[0px]",
        )}
      >
        {/* Display the mock if restaurants is not an arrays */}

        {isAuthWrapperRoute && (
          <div
            className="overflow-y-auto grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 "
            style={{
              height: `calc(100vh - var(--height-header) - ${
                !sm ? "260px" : "220px"
              })`,
              gap: viewMode === "map" ? "0px" : "16px",
            }}
          >
            {restaurantListMock.map((restaurant, index) => (
              <RestaurantCard
                key={`restaurant-mock-${restaurant.address}-${index}`}
                name={restaurant.name}
                address={restaurant.address}
                tags={
                  restaurant?.tags?.filter((it) => typeof it === "string") ?? []
                }
                rate={restaurant.rate}
                image={restaurant.image}
                platform={restaurant.plateform}
                krunshScore={restaurant.krunshScore}
                deliveryTime={restaurant.deliveryTime}
              />
            ))}
          </div>
        )}

        {/* When the gettings restaurants */}

        {(isLoading || !restaurants) && !isAuthWrapperRoute && (
          <div
            className="overflow-y-auto grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 "
            style={{
              height: sm
                ? "calc(100vh - var(--height-header) - 80px)"
                : getHeightMap(drawerState ?? "half-open"),
              gap: viewMode === "map" ? "0px" : "16px",
            }}
          >
            <SkeletonRestaurantListInExplorer />
          </div>
        )}

        {/* Display the mock if restaurants is not an arrays */}

        <div
          onScroll={(e) => fetchMoreOnBottomReached(e.currentTarget)}
          ref={parentRef}
          className="overflow-y-auto overflow-x-hidden"
          style={{
            height: sm
              ? "calc(100vh - var(--height-header) - 80px)"
              : getHeightMap(drawerState ?? "half-open"),
          }}
        >
          <div
            className="flex flex-col"
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {Array.isArray(restaurants) &&
              !isLoading &&
              rowVirtualizer.getVirtualItems().map((virtualItem) => (
                <div
                  className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-[16px]"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${virtualItem?.start ?? 0}px)`,
                  }}
                  key={virtualItem.key}
                >
                  {restaurantsGrid[virtualItem.index].map((restaurant) => (
                    <div
                      ref={rowVirtualizer.measureElement}
                      key={`${virtualItem.key}-${restaurant.id}`}
                      data-index={virtualItem.index}
                      className="pb-[16px]"
                    >
                      <RestaurantCard
                        isFavorite={Boolean(
                          favoriteRestaurantsId.find(
                            (restaurantId) => restaurantId === restaurant.id,
                          ),
                        )}
                        setAddFavoriteThisReastaurantId={
                          setAddFavoriteThisReastaurantId
                        }
                        firstSeen={restaurant.firstSeenAt}
                        name={restaurant.name}
                        address={restaurant?.address ?? ""}
                        tags={
                          restaurant?.categories
                            ?.filter(
                              (it) => typeof it === "string" && it !== "",
                            )
                            ?.filter((it): it is string => it !== null) ?? []
                        }
                        rate={restaurant?.rating ?? ""}
                        image={restaurant?.imageUrl ?? undefined}
                        platform={restaurant.platform.toLowerCase()}
                        krunshScore={restaurant.krunshScore}
                        deliveryTime={restaurant.deliveryTime}
                        id={restaurant.id}
                        onClick={() => {
                          navigate(
                            generatePath("/explorer/restaurant/:restaurantId", {
                              restaurantId: restaurant.id,
                            }),
                          );
                        }}
                        canBeFavorited
                      />
                    </div>
                  ))}
                </div>
              ))}
          </div>
          {isFetching && (
            <div
              className="w-[100%] grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 "
              style={{
                gap: viewMode === "map" ? "0px" : "16px",
              }}
            >
              <SkeletonRestaurantListInExplorer />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default RestaurantList;
